import { Check } from "lucide-react";

/**
 * StepProgress — 3-step indicator used across the evaluation flow.
 *
 * Honest progress only: states are discrete (completed / current / upcoming)
 * and no misleading global percentage is displayed. Step-level percentages
 * were removed because they don't reflect real work done inside step 3
 * (Process Evaluation carries the bulk of the questionnaire).
 *
 * States:
 *   - completed → teal filled disc with a white check
 *   - current   → teal filled disc with a small white inner dot
 *   - upcoming  → outlined disc in muted grey
 */
const DEFAULT_LABELS = ["Company Profile", "Warehouse Landscape", "Process Evaluation"];

export const StepProgress = ({ currentStep = 1, total = 3, labels }) => {
  const names = labels && labels.length === total ? labels : DEFAULT_LABELS;

  return (
    <div
      data-testid="step-progress"
      className="sticky top-16 z-40 w-full bg-white border-b border-[#E5E7EB]"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[11px] font-semibold text-[#1F2937] tracking-wide">
            Step {currentStep} of {total}
            <span className="ml-1.5 font-normal text-[#4B5563]">
              — {names[currentStep - 1]}
            </span>
          </span>
        </div>
        <div className="mt-3 flex items-center">
          {Array.from({ length: total }).map((_, idx) => {
            const n = idx + 1;
            const isCompleted = n < currentStep;
            const isCurrent = n === currentStep;
            const isLast = n === total;
            return (
              <div key={n} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center">
                  <div
                    data-testid={`step-circle-${n}`}
                    data-state={isCurrent ? "current" : isCompleted ? "completed" : "upcoming"}
                    className={[
                      "w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200",
                      isCompleted
                        ? "bg-[#03738C] text-white border border-[#03738C] shadow-sm"
                        : isCurrent
                          ? "bg-[#025979] text-white border border-[#025979] shadow-sm ring-4 ring-[#025979]/10"
                          : "bg-white text-[#9CA3AF] border border-[#D1D5DB]",
                    ].join(" ")}
                  >
                    {isCompleted ? (
                      <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                    ) : isCurrent ? (
                      <span aria-hidden className="w-2 h-2 rounded-full bg-white" />
                    ) : null}
                  </div>
                  <span
                    className={[
                      "mt-1.5 text-[10px] font-medium tracking-wide uppercase transition-colors",
                      isCurrent
                        ? "text-[#025979]"
                        : isCompleted
                          ? "text-[#1F2937]"
                          : "text-[#9CA3AF]",
                    ].join(" ")}
                  >
                    {names[idx]}
                  </span>
                </div>
                {!isLast && (
                  <div
                    className={[
                      "flex-1 h-[2px] mx-2 rounded-full transition-colors duration-200",
                      n < currentStep ? "bg-[#03738C]" : "bg-[#E5E7EB]",
                    ].join(" ")}
                    aria-hidden
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
